/**
 * Web Socket Transport Class
 *
 * Handles WS instance with retry logic and message handling
 *
 * ** Example usage
 * const socket = new SocketInstance("wss://example.com", (message) => {
 *    console.log("Received message:", message);
 * });
 *
 * */

/**
 * Retry logic:
 * - Retry limit: Intinity (previous 30 attempts)
 * - Retry default interval: 60s
 * - Initial retry intervals: 500ms, 1s, 2.5s, 5s, 10s, 30s
 */
const RETRY_LIMIT: number = Infinity;
const RETRY_DEFAULT_INTERVAL: number = 60_000;
const RETRY_INTERVALS: number[] = [500, 1000, 2500, 5000, 10_000, 30_000];

export class SocketInstance {
	public host: string;
	private socket: WebSocket;

	// Retry logic
	private retryCount: number = 0;
	private retryTimer: ReturnType<typeof setTimeout> = null;

	/**
	 * Marks a close that was initiated by our own code (disconnect, reconnect,
	 * logout, unit switch). Such closes must never auto-retry. Any close we did
	 * NOT initiate — an abnormal drop or a clean server-side close due to
	 * session/token expiry or an idle timeout — should reconnect.
	 */
	private intentionalClose: boolean = false;

	/** Create a new socket instance
	 *
	 * @param address - The WSS address to connect to
	 * @param MessageHandler - Function to handle messages
	 * @param AuthTokenGetter - Async Function to get auth token | false if no authentication is required
	 *
	 * */
	constructor(
		private address: string,
		private MessageHandler: (message: any) => void,
		private AuthTokenGetter: false | (() => Promise<string>) = false,
	) {
		this.host = new URL(this.address).hostname;

		this.init();
	}

	/** Get the ready state of the socket */
	public getReadyState = (): number => {
		if (!this.socket) return WebSocket.CLOSED;
		return this.socket.readyState;
	};

	/**
	 * Asynchronously retrieves the WebSocket address, appending authentication parameters if necessary.
	 *
	 * The token is obtained via a provided token getter function, when needed.
	 *
	 * @returns Promise<string> - The full WS address with authentication parameters
	 * @throws An Error if the token is required but cannot be obtained.
	 *  */
	private getWSAddress = async (): Promise<string> => {
		if (!this.AuthTokenGetter) {
			return this.address;
		}

		const token = await this.AuthTokenGetter();
		if (!token) {
			throw new Error(`Failed to get token for host ${this.host}`);
		}
		return `${this.address}?t=${token}`;
	};

	/** Initialize the socket */
	public async init(): Promise<void> {
		if (this.getReadyState() !== WebSocket.CLOSED) {
			console.warn(`[SOCKET] Socket to host ${this.host} is already connected`);
			return;
		}

		this.clearRetryTimer();

		// Get the WS address and create a new socket
		await this.getWSAddress()
			.then((address) => {
				this.intentionalClose = false;
				this.socket = new WebSocket(address);
				this.setEventHandlers();
			})
			.catch((err) => {
				console.error(`[SOCKET] Connection failed to ${this.address}`, err);
				this.retry();
			});
	}

	/**
	 * Sets WebSocket event handlers.
	 */
	private setEventHandlers(): void {
		if (!this.socket) return;

		this.socket.onopen = this.onOpen;
		this.socket.onmessage = this.onMessage;
		this.socket.onerror = this.onError;
		this.socket.onclose = this.onClose;
	}

	private onOpen = () => {
		console.log(
			`%c[SOCKET] WebSocket connected:`,
			'background-color: green; color: white',
			this.host,
		);
		this.resetRetry();
	};

	private onMessage = (msg: MessageEvent) => {
		const message = JSON.parse(msg.data);
		// console.debug("[SOCKET][%s] Received:", this.host, message);
		this.MessageHandler(message);
	};

	private onError = (err: Event) => {
		console.error(`[SOCKET] ERROR: [${this.host}] Socket closed with error: ${err.type}`, err);
	};

	private onClose = (e: CloseEvent) => {
		console.warn(`[SOCKET] Socket to host ${this.host} is closed.
            Code: ${e.code} Reason: ${e.reason}; wasClean: ${e.wasClean}; intentional: ${this.intentionalClose}`);

		this.socket = null;

		// A close we initiated ourselves (disconnect / reconnect / logout /
		// unit switch) must never auto-retry.
		if (this.intentionalClose) {
			this.intentionalClose = false;
			return;
		}

		// Everything else is a close we did NOT initiate: either an abnormal
		// drop (wasClean === false) or a *clean* close from the server because
		// the session/token expired or the connection idled out. Reconnect in
		// all these cases — the retry runs through `getWSAddress()`, which
		// fetches a fresh token before opening the new socket.
		this.retry();
	};

	/** Send data to the socket
	 *
	 * @param data - The data to send
	 * @returns boolean - True if there is socket to handle data send, false otherwise
	 * */
	public send(data: any): boolean {
		if (this.getReadyState() !== WebSocket.OPEN) {
			return false;
		}

		this.socket.send(JSON.stringify(data));
		return true;
	}

	/** Close the socket */
	public close(): void {
		this.intentionalClose = true;
		if (this.socket) {
			this.socket.close();
		}
		this.clearRetryTimer();
	}

	/** Reconnect the socket */
	public reconnect(): void {
		this.close();
		this.init();
	}

	/** Sets retry for connecting to the socket after specific interval */
	public retry(): void {
		if (this.retryCount >= RETRY_LIMIT) {
			console.error(`[SOCKET] Socket to host ${this.host} has reached retry limit`);
			return;
		}

		this.setRetryTimer();
	}

	/** Get the retry interval */
	private getRetryInterval(): number {
		let interval: number = RETRY_INTERVALS[this.retryCount] || RETRY_DEFAULT_INTERVAL;
		interval += Math.floor(Math.random() * 0.5 * interval); // Interval + 0-50% randomization
		return interval;
	}

	/** Set the retry timer */
	private setRetryTimer(): void {
		let interval: number = this.getRetryInterval();
		this.clearRetryTimer();

		console.log(`[SOCKET] Will retry #${this.retryCount + 1}/${RETRY_LIMIT} in ${interval}ms
            connection to host ${this.host}`);

		this.retryTimer = setTimeout(() => {
			console.log(`[SOCKET] Retry #${this.retryCount + 1}/${RETRY_LIMIT}
                connection to host ${this.host}`);
			this.reconnect();
			this.retryCount++;
		}, interval);
	}

	/** Clear the retry timer */
	private clearRetryTimer(): void {
		if (this.retryTimer) {
			clearTimeout(this.retryTimer);
			this.retryTimer = null;
		}
	}

	/** Reset the retry timer and count */
	private resetRetry(): void {
		this.clearRetryTimer();
		this.retryCount = 0;
	}
}
